import React, { useState, useEffect, ReactNode } from 'react';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Collapse,
  ListItemButton,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAnalytics } from '../contexts/AnalyticsContext';
import { useValidation } from '../contexts/ValidationContext';
import { useLicense } from '../contexts/LicenseContext';
import { useOptionalBrewery } from '../contexts/BreweryContext';
import logo from '../logo.png';

import {
  Menu as MenuIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Help as HelpIcon,
  ContactMail as ContactIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  LocalBar as LocalBarIcon,
  Label as LabelIcon,
  Science as ScienceIcon,
  Calculate as CalculateIcon,
  EmojiEvents as EmojiEventsIcon,
  CompareArrows as CompareArrowsIcon,
  MenuBook as MenuBookIcon,
  People as PeopleIcon,
  ExpandLess,
  ExpandMore,
  WorkspacePremium as CompetitionsIcon,
  Summarize as SummarizeIcon,
  FactCheck as FactCheckIcon,
  ShoppingCart as ShoppingCartIcon,
  Checklist as ChecklistIcon,
  History as HistoryIcon,
  Grain as GrainIcon,
  Biotech as BiotechIcon,
} from '@mui/icons-material';

const drawerWidth = 200;

interface MainMenuProps {
  children: ReactNode;
  onMenuToggle: (isOpen: boolean) => void;
}

const HIDDEN_MENU_ITEMS_KEY = 'Settings.hiddenMenuItems';

export function getHiddenMenuItems(): string[] {
  try { return JSON.parse(localStorage.getItem(HIDDEN_MENU_ITEMS_KEY) || '[]'); } catch { return []; }
}

export function setHiddenMenuItems(paths: string[]): void {
  localStorage.setItem(HIDDEN_MENU_ITEMS_KEY, JSON.stringify(paths));
}

const MainMenu: React.FC<MainMenuProps> = ({ children, onMenuToggle }) => {
  const theme = useTheme();
  // Below `sm` (600px) = phone: overlay drawer that floats over content.
  // At/above 600px = desktop/tablet: persistent drawer that pushes content and
  // reflows to full width when closed. (Was `md`/900px, which wrongly treated
  // half-width and DevTools-docked desktop windows as mobile.) Consistent with
  // the useIsMobile hook.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(!isMobile);
  const [isSettingsSectionOpen, setIsSettingsSectionOpen] = useState(false);
  const [hiddenItems, setHiddenItemsState] = useState<string[]>(getHiddenMenuItems);

  // Re-read when Settings saves (same-tab custom event)
  useEffect(() => {
    const onChanged = () => setHiddenItemsState(getHiddenMenuItems());
    window.addEventListener('menuItemsChanged', onChanged);
    return () => window.removeEventListener('menuItemsChanged', onChanged);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEvent } = useAnalytics();
  const { hasValidLicense, hasValidApiKey } = useValidation();
  const { isBreweryAdmin, isBreweryMember, breweryId } = useLicense();
  const isBreweryAccount = isBreweryAdmin || isBreweryMember;
  const brewery = useOptionalBrewery();

  const allMainMenuItems = [
    {
      text: 'Batch Plan',
      icon: <CalendarIcon />,
      path: '/batch-plan',
      viewKey: 'batch-plan',
    },
    {
      text: 'Stock Plan',
      icon: <InventoryIcon />,
      path: '/stock-plan',
      viewKey: 'stock-plan',
    },
    { text: 'Search', icon: <SearchIcon />, path: '/batch-search', viewKey: 'batch-search' },
    { text: 'Compare', icon: <CompareArrowsIcon />, path: '/compare', viewKey: 'compare' },
    ...(isBreweryAccount ? [{ text: 'Recipes', icon: <MenuBookIcon />, path: '/recipes', viewKey: 'recipes' }] : []),
    { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory', viewKey: 'inventory' },
    { text: 'Yeast Bank', icon: <ScienceIcon />, path: '/yeast-bank', viewKey: 'yeast-bank' },
    { text: 'Statistics', icon: <AssessmentIcon />, path: '/statistics', viewKey: 'statistics' },
    { text: 'Tap List', icon: <LocalBarIcon />, path: '/tap-list', viewKey: 'tap-list' },
    { text: 'Labels', icon: <LabelIcon />, path: '/batch-labels', viewKey: 'batch-labels' },
    { text: 'Tools', icon: <CalculateIcon />, path: '/tools', viewKey: 'tools' },
    {
      text: 'Rank List',
      icon: <EmojiEventsIcon />,
      path: '/rank-list',
      viewKey: 'rank-list',
    },
    {
      text: 'Competitions',
      icon: <CompetitionsIcon />,
      path: '/competitions',
      viewKey: 'competitions',
    },
    ...(isBreweryAccount ? [
      { text: 'Brew Day', icon: <ChecklistIcon />, path: '/brew-day', viewKey: 'brew-day' },
      { text: 'Quality', icon: <FactCheckIcon />, path: '/quality', viewKey: 'quality' },
      { text: 'Malt Lots', icon: <GrainIcon />, path: '/malt-lots', viewKey: 'malt-lots' },
      { text: 'Yeast Lots', icon: <BiotechIcon />, path: '/yeast-lots', viewKey: 'yeast-lots' },
      { text: 'Orders', icon: <ShoppingCartIcon />, path: '/purchase-orders', viewKey: 'purchase-orders' },
      { text: 'Production', icon: <SummarizeIcon />, path: '/production-report', viewKey: 'production-report' },
    ] : []),
  ];

  // Filter menu items by permissions for brewery members.
  // Admin and homebrewer see all items; a member only sees views the brewery
  // admin granted them access to (ungated items with no viewKey stay visible).
  const mainMenuItems = isBreweryMember
    ? allMainMenuItems.filter(item => !('viewKey' in item) || !item.viewKey || (brewery?.hasViewAccess(item.viewKey) ?? false))
    : allMainMenuItems;

  const settingsMenuItems = [
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    ...(isBreweryAdmin ? [
      { text: 'Users', icon: <PeopleIcon />, path: '/brewery-users' },
      { text: 'Audit Log', icon: <HistoryIcon />, path: '/audit-log' },
    ] : []),
    { text: 'Help', icon: <HelpIcon />, path: '/help' },
    { text: 'Contact', icon: <ContactIcon />, path: '/contact' },
  ];

  const toggleDrawer = () => {
    const newState = !isDrawerOpen;
    setIsDrawerOpen(newState);
    onMenuToggle(newState);
  };

  const handleNavigation = (path: string) => {
    const menuItem = [...mainMenuItems, ...settingsMenuItems].find(item => item.path === path);
    if (menuItem) {
      trackEvent('menu_navigation', {
        menu_item: menuItem.text,
        path: path
      });
    }

    // Auto-collapse settings section when navigating to main menu items
    const isMainMenuItem = mainMenuItems.some(item => item.path === path);
    if (isMainMenuItem) {
      setIsSettingsSectionOpen(false);
    }

    navigate(path);

    // Close drawer on mobile after navigation
    if (isMobile) {
      setIsDrawerOpen(false);
      onMenuToggle(false);
    }
  };

  const toggleSettingsSection = () => {
    setIsSettingsSectionOpen(!isSettingsSectionOpen);
  };

  // Auto-expand settings section if user is on a settings page and missing prerequisites
  useEffect(() => {
    const settingsPaths = settingsMenuItems.map(item => item.path);
    const isOnSettingsPage = settingsPaths.includes(location.pathname);
    const missingPrerequisites = !hasValidLicense || !hasValidApiKey;

    if (isOnSettingsPage && missingPrerequisites) {
      setIsSettingsSectionOpen(true);
    }
  }, [location.pathname, hasValidLicense, hasValidApiKey]);

  // Handle drawer state on screen size changes
  useEffect(() => {
    setIsDrawerOpen(!isMobile);
    onMenuToggle(!isMobile);
  }, [isMobile]);

  const renderMenuItems = (items: typeof mainMenuItems) => {
    return items.map((item) => (
      <ListItem
        key={item.text}
        onClick={() => handleNavigation(item.path)}
        sx={{ 
          cursor: 'pointer',
          backgroundColor: location.pathname === item.path ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
          '&:hover': {
            backgroundColor: location.pathname === item.path 
              ? 'rgba(25, 118, 210, 0.12)' 
              : 'rgba(0, 0, 0, 0.04)',
          },
          borderLeft: location.pathname === item.path ? '4px solid #1976d2' : '4px solid transparent',
          '& .MuiListItemIcon-root': {
            color: location.pathname === item.path ? '#1976d2' : 'inherit',
          },
          '& .MuiListItemText-primary': {
            color: location.pathname === item.path ? '#1976d2' : 'inherit',
            fontWeight: location.pathname === item.path ? 600 : 400,
            minHeight: '24px',
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          },
        }}
      >
        <ListItemIcon>
          {item.icon}
        </ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItem>
    ));
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: '100%',
          left: 0,
          transition: 'all 0.2s',
        }}
      >
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src={logo} 
              alt="Batchfather Logo" 
              style={{ 
                height: '32px', 
                marginRight: '12px',
                objectFit: 'contain'
              }} 
            />
            <Typography variant="h6" component="div">
              Batchfather Dashboard
            </Typography>
            {isBreweryAccount && (
              <Chip
                label={isBreweryAdmin ? 'Brewery Admin' : 'Brewery Member'}
                size="small"
                sx={{ ml: 1, color: 'inherit', borderColor: 'rgba(255,255,255,0.5)' }}
                variant="outlined"
              />
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor="left"
        open={isDrawerOpen}
        onClose={() => {
          if (isMobile) {
            setIsDrawerOpen(false);
            onMenuToggle(false);
          }
        }}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {renderMenuItems(mainMenuItems.filter(item => !hiddenItems.includes(item.path)))}
          </List>
          <Divider />
          <List>
            <ListItemButton onClick={toggleSettingsSection}>
              {isSettingsSectionOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={isSettingsSectionOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {/* Settings item first, then Help and Contact. Items hidden via
                    "Menu Items" in Settings are fully hidden, not moved here. */}
                {[settingsMenuItems[0],
                  ...settingsMenuItems.slice(1),
                ].map((item) => (
                  <ListItem
                    key={item.text}
                    onClick={() => handleNavigation(item.path)}
                    sx={{
                      cursor: 'pointer',
                      pl: 4,
                      backgroundColor: location.pathname === item.path ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      '&:hover': {
                        backgroundColor: location.pathname === item.path
                          ? 'rgba(25, 118, 210, 0.12)'
                          : 'rgba(0, 0, 0, 0.04)',
                      },
                      borderLeft: location.pathname === item.path ? '4px solid #1976d2' : '4px solid transparent',
                      '& .MuiListItemIcon-root': {
                        color: location.pathname === item.path ? '#1976d2' : 'inherit',
                      },
                      '& .MuiListItemText-primary': {
                        color: location.pathname === item.path ? '#1976d2' : 'inherit',
                        fontWeight: location.pathname === item.path ? 600 : 400,
                        minHeight: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      },
                    }}
                  >
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          mt: { xs: '56px', sm: '96px' },
          // The persistent drawer always reserves its width in the flex row, even
          // when closed. On desktop, pull content left by that width when the menu
          // is closed so it reclaims the empty column and fills the viewport.
          // Mobile uses an overlay drawer that reserves no space, so no shift.
          ml: !isMobile && !isDrawerOpen ? `-${drawerWidth}px` : 0,
          // Match the drawer's own open/close animation exactly (same easing and
          // duration MUI uses to slide the panel) so the reflow feels synced.
          transition: (theme) => theme.transitions.create('margin', {
            easing: isDrawerOpen ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
            duration: isDrawerOpen ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default MainMenu; 